// TablesUtils.js - Utility functions for Tables component
import axios from "../../utils/axios";

export const TRUNCATE_LENGTH = 32;



export function getDisplayValue(v) {
  if (typeof v !== "string" || v.length <= TRUNCATE_LENGTH) return v;
  return v.slice(0, TRUNCATE_LENGTH) + "...";
}

// Parses a possibly comma-separated, quote-wrapped OrderID string into clean individual IDs
export function parseOrderIds(raw) {
  if (!raw) return [];
  return String(raw)
    .split(',')
    .map(s => s.trim().replace(/^"+|"+$/g, ''))
    .filter(Boolean);
}

export function sanitizeRow(row) {
  const sanitized = {};
  Object.entries(row).forEach(([k, v]) => {
    sanitized[k] = typeof v === "string" ? v.trim() : v;
  });
  return sanitized;
}

// Converts a Python dict/list literal string (single quotes, True/False/None)
// into valid JSON text so it can be parsed.
function pythonLiteralToJSON(raw) {
  let s = raw.trim();
  s = s.replace(/\bTrue\b/g, 'true').replace(/\bFalse\b/g, 'false').replace(/\bNone\b/g, 'null');
  // Convert single-quoted strings to double-quoted, escaping any inner double quotes
  s = s.replace(/'((?:[^'\\]|\\.)*)'/g, (match, inner) => `"${inner.replace(/"/g, '\\"')}"`);
  return s;
}

// Parses GuestInfo (array of guest objects, possibly stored as a Python literal string)
export function parseGuestInfo(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string') return [];
  try {
    return JSON.parse(raw);
  } catch (e) {
    try {
      return JSON.parse(pythonLiteralToJSON(raw));
    } catch (e2) {
      return [];
    }
  }
}

// Coerce known field types for specific tables to avoid validation errors
export function coerceTypesForTable(currentTable, row) {
  const payload = { ...row };
  const path = (currentTable?.path || "").toLowerCase();

  // Generic coercion using table_types metadata
  const types = currentTable?.table_types || {};
  const toBool = (val) => {
    if (typeof val === "boolean") return val;
    if (typeof val === "number") return val !== 0;
    if (typeof val === "string") {
      const s = val.trim().toLowerCase();
      if (["true", "1", "yes", "y"].includes(s)) return true;
      if (["false", "0", "no", "n"].includes(s)) return false;
    }
    return undefined;
  };
  Object.entries(payload).forEach(([field, value]) => {
    const meta = types[field]; // { infoType, dataType }
    if (!meta) return;
    const infoType = String(meta.infoType || '').toLowerCase();
    const dataType = String(meta.dataType || '').toLowerCase();

    if (value === undefined) return;
    // DisplayPriority: null means "random" and must be sent explicitly
    if (infoType === 'displaypriority') {
      payload[field] = (value === null || value === '') ? null : Number(value);
      return;
    }
    
    if (infoType === 'date') {
      payload[field] = value ? value : null;
      return;
    }

    // Boolean
    if (infoType === 'boolean' || dataType === 'bool' || dataType === 'boolean') {
      const b = toBool(value);
      if (b === undefined) delete payload[field]; else payload[field] = b;
      return;
    }
    // Number (int/float)
    if (infoType === 'number' || dataType === 'int' || dataType === 'float' || dataType === 'number') {
      if (value === null || value === '') { delete payload[field]; return; }
      if (typeof value === 'number') return;
      const n = (dataType === 'int') ? parseInt(value, 10) : parseFloat(value);
      if (Number.isNaN(n)) delete payload[field]; else payload[field] = n;
      return;
    }
    // DateTime - pass through as string; ensure empty -> drop
    if (infoType === 'datetime') {
      if (value === null || value === '') delete payload[field];
      return;
    }
  });

  // Lounge-specific coercions
  if (path.startsWith("/lounges")) {
    // Boolean fields
    const boolFields = ["AfterSecurityCheck", "AIMSSupport"];
    boolFields.forEach((f) => {
      if (payload[f] === undefined) {
        // Checkbox not checked → set false
        payload[f] = false;
      } else {
        const b = toBool(payload[f]);
        payload[f] = b === undefined ? false : b;
      }
    });

    // Numeric fields
    const numFields = ["EntryFee", "EntryFeeChildren", "Capacity", "CapacityThreshold"];
    numFields.forEach((f) => {
      if (payload[f] !== undefined) {
        const v = payload[f];
        if (v === null || v === "") {
          delete payload[f];
          return;
        }
        if (typeof v === "number") return;
        const n = f === "Capacity" ? parseInt(v, 10) : parseFloat(v);
        if (Number.isNaN(n)) {
          delete payload[f];
        } else {
          payload[f] = n;
        }
      }
    });
  }

  return payload;
}

export function inferColumnType(rows, col) {
  for (let row of rows) {
    const val = row[col];
    if (val !== null && val !== undefined) {
      if (typeof val === "number") return "number";
      if (!isNaN(Number(val)) && val !== "") return "number";
      return "string";
    }
  }
  return "string";
}

export function getSortedRows(rows, sortColumn, sortDirection, inferTypeFn) {
  if (!sortColumn) return rows;
  const type = inferTypeFn(rows, sortColumn);
  const sorted = [...rows].sort((a, b) => {
    const va = a[sortColumn];
    const vb = b[sortColumn];
    if (type === "number") {
      return (Number(va) - Number(vb)) * (sortDirection === "asc" ? 1 : -1);
    } else {
      return String(va || "").localeCompare(String(vb || "")) * (sortDirection === "asc" ? 1 : -1);
    }
  });
  return sorted;
}

export function getFilteredRows(inputRows, searchText, searchColumn, keyFields, visibleFields) {
  if (!searchText.trim()) return inputRows;
  const lower = searchText.toLowerCase();
  if (searchColumn) {
    return inputRows.filter(row => {
      const val = row[searchColumn];
      return val !== undefined && val !== null && String(val).toLowerCase().includes(lower);
    });
  } else {
    return inputRows.filter(row =>
      [...keyFields, ...visibleFields.filter(k => !keyFields.includes(k))].some(col => {
        const val = row[col];
        return val !== undefined && val !== null && String(val).toLowerCase().includes(lower);
      })
    );
  }
}

// Maps table tags to the date column used for date-range filtering
export const DATE_FILTER_FIELDS = {
  Transaction: 'TimeStamp',
  Orders: 'FromDateTime',
};

export function getDateFilterField(currentTable) {
  return DATE_FILTER_FIELDS[currentTable?.tag] || null;
}

// Filters rows by an inclusive date range on the given field.
// dateFrom/dateTo are 'YYYY-MM-DD' strings from <input type="date">.
export function getFilteredRowsByDateRange(rows, dateField, dateFrom, dateTo) {
  if (!dateField || (!dateFrom && !dateTo)) return rows;
  const fromTime = dateFrom ? new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate(), 0, 0, 0, 0).getTime() : null;
  const toTime = dateTo ? new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate(), 23, 59, 59, 999).getTime() : null;
  return rows.filter(row => {
    const raw = row[dateField];
    if (!raw) return false;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) return false;
    if (fromTime !== null && t < fromTime) return false;
    if (toTime !== null && t > toTime) return false;
    return true;
  });
}

// Returns true if the lounge's ActivityStatus is exactly "active" (case-insensitive, trimmed).
// Deliberately an exact match — "Active(Purchase Unavailable)" must NOT count as active.
export function isLoungeActive(row) {
  const status = (row?.ActivityStatus || '').trim().toLowerCase();
  return status === 'active';
}

export function getFilteredRowsByActiveStatus(rows, activeOnly) {
  if (!activeOnly) return rows;
  return rows.filter(isLoungeActive);
}

export async function fetchTableData(currentTable, currentUser) {
  if (!currentTable || !currentUser) return {};
  try {
    const { getAuth } = await import("firebase/auth");
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    const [dataRes, metaRes] = await Promise.all([
      axios.get(currentTable.path, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${currentTable.path}/meta`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    if (!Array.isArray(dataRes.data)) {
      throw new Error("Data format is not an array");
    }
    return {
      rows: dataRes.data,
      fillableFields: metaRes.data.fillable || [],
      keyFields: metaRes.data.key_fields || [],
      visibleFields: metaRes.data.visible || [],
      hiddenFields: metaRes.data.hidden_fields || [],
      recordInsertAllowed: !!metaRes.data.record_insert_allowed,
    };
  } catch (err) {
    throw err;
  }
}

// Turns FastAPI errors (string or validation list) into one readable message
export function extractErrorMessage(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map(x => {
      const field = Array.isArray(x.loc) ? x.loc[x.loc.length - 1] : '';
      return field ? `${field}: ${x.msg}` : x.msg;
    }).join('\n');
  }
  return null;
}

// Client-side check for Lounge display priority; returns { error, needsConfirm }
export function validateDisplayPriority(row, original) {
  const p = row.DisplayPriority;
  if (p === null || p === undefined || p === '') return { error: null, needsConfirm: false };
  const n = Number(p);
  if (!Number.isInteger(n) || n < 1 || n > 10) {
    return { error: 'Display priority must be between 1st and 10th, or random.', needsConfirm: false };
  }
  const start = row.DisplayPriorityStart || null;
  const end = row.DisplayPriorityEnd || null;
  const today = new Date().toISOString().slice(0, 10);   // UTC, same as the server
  const changed = !original
    || original.DisplayPriority !== n
    || (original.DisplayPriorityStart || null) !== start
    || (original.DisplayPriorityEnd || null) !== end;
  if (start && end && end < start) {
    return { error: "The end date can't be before the start date.", needsConfirm: false };
  }
  if (changed && end && end < today) {
    return { error: 'The end date is in the past. Choose today or a later date.', needsConfirm: false };
  }
  return { error: null, needsConfirm: changed && !end };
}
// Error handling utilities
export function handleAxiosError(err) {
  console.error("API operation failed:", err);

  if (err.response) {
    const status = err.response.status;
    let message = "Operation failed.";
    if (status === 400) message = "Bad Request – Could not complete operation. Please check the data.";
    else if (status === 401) message = "Unauthorized – Please log in again.";
    else if (status === 403) message = "Forbidden – You don't have permission to perform this action.";
    else if (status === 404) message = "Not Found – The item might not exist or was already removed.";
    else if (status === 409) message = "Conflict – this change clashes with existing data.";
    else if (status === 500) message = "Server error – Please try again later.";

    // Prefer the server's plain-language explanation when there is one
    const serverMessage = extractErrorMessage(err);
    if (serverMessage && [400, 409, 422].includes(status)) message = serverMessage;

    alert(message);
  } else {
    alert("Network error – Please check your connection.");
  }
}

// Foreign key utilities
export function buildForeignKeyMap(foreignKeyRelations) {
  const fkMap = {};
  (foreignKeyRelations || []).forEach(rel => {
    Object.entries(rel).forEach(([local, ref]) => {
      const [localTable, localCol] = local.split('.');
      const [refTable, refCol] = ref.split('.');
      fkMap[localCol] = { refTable, refCol };
    });
  });
  return fkMap;
}

export async function fetchForeignKeyData(col, value, fkMap, tableEndpoints, currentUser) {
  const fk = fkMap[col];
  if (!fk || !value) return null;
  
  try {
    const token = await currentUser.firebaseUser.getIdToken();
    const refTableMeta = tableEndpoints.find(t => t.tag === fk.refTable);
    if (!refTableMeta) throw new Error("Referenced table not found");
    
    const res = await axios.get(`${refTableMeta.path}/${value}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err) {
    return { error: err.message };
  }
}

// CRUD operations
export async function createTableRow(currentTable, newRow, currentUser) {
  const token = await currentUser.firebaseUser.getIdToken();
  const sanitizedRow = sanitizeRow(newRow);
  const payload = coerceTypesForTable(currentTable, sanitizedRow);
  console.log(currentTable.path);
  const res = await axios.post(currentTable.path, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  
  return res.data;
}

export async function updateTableRow(currentTable, editingId, editData, currentUser) {
  const token = await currentUser.firebaseUser.getIdToken();
  const sanitized = sanitizeRow(editData || {});
  const payload = coerceTypesForTable(currentTable, sanitized);
  const res = await axios.put(`${currentTable.path}/${editingId}`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

export async function deleteTableRow(currentTable, row, keyFields, currentUser) {
  const token = await currentUser.firebaseUser.getIdToken();
  
  if (keyFields.length === 1) {
    const id = row[keyFields[0]];
    if (!id) {
      throw new Error("Delete failed: missing ID");
    }
    await axios.delete(`${currentTable.path}/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } else {
    const body = {};
    keyFields.forEach((k) => (body[k] = row[k]));
    await axios.delete(`${currentTable.path}`, {
      headers: { Authorization: `Bearer ${token}` },
      data: body,
    });
  }
}

// Analytics utilities
export async function fetchAnalyticsColumns(currentTable, currentUser) {
  if (!currentTable || !currentUser) return [];
  
  try {
    const { getAuth } = await import("firebase/auth");
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    const tableName = currentTable.path.replace(/^\//, "");
    const res = await axios.get(`/api/tables/analytics_columns/${tableName}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.analytics_columns || [];
  } catch (err) {
    console.error("Failed to fetch analytics columns:", err);
    return [];
  }
}

export function computeAnalyticsData(rows, selectedAnalyticsColumn) {
  if (!selectedAnalyticsColumn || !rows.length) return [];
  
  const counts = {};
  rows.forEach(row => {
    const val = row[selectedAnalyticsColumn] ?? "Unknown";
    counts[val] = (counts[val] || 0) + 1;
  });
  
  return Object.entries(counts).map(([name, value]) => ({ name, value }));
}

// Category and table organization utilities
export const CATEGORY_LABELS = {
  data_management: 'Data',
  portal_management: 'Portal',
  sales_management: 'Sales',
};

export const CATEGORY_ORDER = ['data_management', 'portal_management', 'sales_management'];

export function groupTablesByCategory(tableEndpoints) {
  return tableEndpoints.reduce((acc, t) => {
    const cat = t.table_category || 'data_management';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});
}

// AI Prefill configuration
export const TABLE_PREFILL_CONFIG = {
  airport: {
    endpoint: "/llm-prefill/prefill_new_airport",
    triggerField: "IATACode",
    triggerLength: 3,
    requestBody: (row) => ({ iata_code: row.IATACode }),
    buttonLabel: (row) =>
      row.IATACode && row.IATACode.length === 3
        ? "Prefill with AI"
        : "Prefill with AI",
    isEnabled: (row) => row.IATACode && row.IATACode.length === 3,
  },
  country: {
    endpoint: "/llm-prefill/prefill_new_country",
    triggerField: "CountryName",
    triggerLength: 2,
    requestBody: (row) => ({ country_name: row.CountryName }),
    buttonLabel: (row) =>
      row.CountryName && row.CountryName.length > 1
        ? "Prefill with AI"
        : "Use AI to Prefill: add country name",
    isEnabled: (row) => row.CountryName && row.CountryName.length > 1,
  },
  // Add more tables here as needed
};

export async function performAIPrefill(currentTable, newRow, currentUser) {
  const { getAuth } = await import("firebase/auth");
  const auth = getAuth();
  const token = await auth.currentUser.getIdToken();
  const tableKey = currentTable?.path?.replace(/^\//, "");
  const config = TABLE_PREFILL_CONFIG[tableKey];
  
  if (!config) throw new Error("No AI prefill configuration found for this table");
  if (!config.isEnabled(newRow)) throw new Error("AI prefill not enabled for current data");
  
  const res = await axios.post(
    config.endpoint,
    config.requestBody(newRow),
    { headers: { Authorization: `Bearer ${token}` } }
  );
  
  if (res.data && res.data.prefill) {
    return res.data.prefill;
  } else {
    throw new Error("No prefill data returned");
  }
}

// Amenity-specific utilities
export async function fetchAmenityOptions(currentUser) {
  try {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    const res = await axios.get('/amenity-title/', { headers: { Authorization: `Bearer ${token}` } });
    return (res.data || []).map(item => item.AmenityName);
  } catch (err) {
    console.error('Failed to fetch amenity options:', err);
    return [];
  }
}

// UI Component utilities
export async function handleCreateWrapper(currentTable, newRow, currentUser, callbacks) {
  try {
    if (callbacks?.onStart) callbacks.onStart();
    const token = await currentUser.firebaseUser.getIdToken();
    const sanitizedRow = sanitizeRow(newRow || {});
    const payload = coerceTypesForTable(currentTable, sanitizedRow);
    console.log('--------------------');
    console.log(payload);
    console.log('--------------------');

    const res = await axios.post(currentTable.path+"/", payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (callbacks?.onSuccess) callbacks.onSuccess(res.data);
    return res.data;
  } catch (err) {
    const detail = extractErrorMessage(err) || err?.message || "Unknown error";
    if (callbacks?.onError) callbacks.onError(detail);
    throw err;
  }
}
export async function handleNotifyWrapper(currentTable, row, currentUser, customMessage) {
  try {
    const token = await currentUser.firebaseUser.getIdToken();
    
    // Build message with custom data from modal
    const sampleMessage = {
      Username: row["Username"], // Static
      LoungeID: row["LoungeID"], // Static
      LoungeName: row["LoungeName"], // Static
      TotalGuests: row["TotalGuests"], // Static
      UserRespond: "pending", // Static
      Title: customMessage.Title,
      Image: customMessage.Image,
      Body: customMessage.Body,
      Device_Token: row["Device_Token"],
      Category: "waitlist", // Static
      Expiration: customMessage.Expiration, 
    };

    const PhoneNumber = row["PhoneNumber"];
    const Communication = row["Communication"]; 

    const sanitizedRow = sanitizeRow(sampleMessage);
    
    const payload = coerceTypesForTable(currentTable, sanitizedRow);
    
    const res = await axios.post("/notification/", {
      ...payload,
      PhoneNumber: PhoneNumber,
      Communication: Communication,
      }, { headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err) {
    const detail = err?.response?.data?.detail || err?.message || "Unknown error";
    throw err;
  }
}
export async function handleDeleteWrapper(currentTable, row, keyFields, currentUser, callbacks) {
  if (!window.confirm("Delete this row?")) return false;
  try {
    const token = await currentUser.firebaseUser.getIdToken();
    if (keyFields.length === 1) {
      const id = row[keyFields[0]];
      if (!id) {
        alert("Delete failed: missing ID");
        return false;
      }
      await axios.delete(`${currentTable.path}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      const body = {};
      keyFields.forEach((k) => (body[k] = row[k]));
      await axios.delete(`${currentTable.path}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: body,
      });
    }
    if (callbacks?.onSuccess) callbacks.onSuccess(row, keyFields);
    return true;
  } catch (err) {
    console.error("Delete failed:", err);
    handleAxiosError(err);
    return false;
  }
}

export function handleStartEdit(row, keyFields, callbacks) {
  const idKey = keyFields[0] || Object.keys(row)[0];
  if (callbacks.onStartEdit) {
    callbacks.onStartEdit(row[idKey], row);
  }
  return { editingId: row[idKey], editData: row };
}

export async function handleCommitEdit(currentTable, editingId, editData, keyFields, currentUser, callbacks) {
  if (!editingId) {
    alert("Update failed: missing ID");
    return false;
  }

  try {
    const token = await currentUser.firebaseUser.getIdToken();
    const sanitized = sanitizeRow(editData || {});
    const payload = coerceTypesForTable(currentTable, sanitized);
    const res = await axios.put(`${currentTable.path}/${editingId}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (callbacks.onSuccess) {
      callbacks.onSuccess(res.data, editingId, keyFields);
    }
    return res.data;
  } catch (err) {
    handleAxiosError(err);
    return false;
  }
}

export function renderTableHeader(rows, keyFields, visibleFields, hiddenFields = []) {
  if (!rows.length) return [];
  // Always show all keyFields, then all visibleFields not in keyFields, minus hidden ones
  const orderedKeys = [
    ...keyFields,
    ...visibleFields.filter((k) => !keyFields.includes(k)),
  ].filter((k) => !hiddenFields.includes(k));
  return orderedKeys;
}
